import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronRight, Lock } from "lucide-react";

/**
 * HoldsWidget — custom renderer for widget_key = "holds".
 *
 * Aggregator payload:
 *   payload.data = { count, rows: [{tour_id, tour_display_name, tour_number,
 *                                    asset_id, coach_identifier, hold_level,
 *                                    start_date}], label }
 * Renders up to 5 rows. NO mutations (Promote / Release / Bump) — this
 * widget is read-only per Wave 2A brief. "View All Holds" click-through
 * goes to the existing /tours list (there is no dedicated /holds page).
 */

const LEVEL_STYLE = {
  soft:   "bg-chrome-mist text-carbon",
  first:  "bg-coach-blue text-white",
  second: "bg-amber-hold text-carbon",
};
const LEVEL_LABEL = { soft: "Soft", first: "1st", second: "2nd" };

const Row = ({ r }) => {
  const to = r.tour_id ? `/tours/${r.tour_id}` : null;
  const tourText = r.tour_display_name
    || (r.tour_number ? `Tour ${r.tour_number}` : "Unlinked hold");
  const coachText = r.coach_identifier || "—";
  const badge = (
    <span
      data-testid={`hold-row-level-${r.hold_level}`}
      className={`inline-block text-[9.5px] uppercase tracking-widest rounded-sm px-1.5 py-0.5 ${LEVEL_STYLE[r.hold_level] || "bg-chrome-mist text-carbon"}`}
    >
      {LEVEL_LABEL[r.hold_level] || r.hold_level}
    </span>
  );
  const body = (
    <div className="flex items-center gap-2 py-1.5">
      {badge}
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-carbon dark:text-road-white truncate">
          {tourText}
        </div>
        <div className="text-[10.5px] text-steel truncate">
          {coachText} · {r.start_date || "—"}
        </div>
      </div>
      {to && <ChevronRight size={12} className="text-steel shrink-0" />}
    </div>
  );
  return to ? (
    <Link
      to={to}
      data-testid={`hold-row-${r.id}`}
      className="block px-2 rounded-sm hover:bg-road-white dark:hover:bg-carbon/40 transition"
    >
      {body}
    </Link>
  ) : (
    <div data-testid={`hold-row-${r.id}`} className="px-2">{body}</div>
  );
};

const Restricted = () => (
  <div
    data-testid="widget-holds"
    data-widget-tone="denied"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[172px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <Lock size={11} /> Holds
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver">Restricted</div>
  </div>
);

export default function HoldsWidget({ payload }) {
  if (payload?.unavailable_reason === "permission_denied") return <Restricted />;
  const count = payload?.data?.count ?? 0;
  const rows = payload?.data?.rows || [];

  return (
    <div
      data-testid="widget-holds"
      className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-4 min-h-[172px] flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <CalendarClock size={11} /> Holds
        </div>
        <div className="text-[10.5px] text-steel tabular-nums" data-testid="widget-holds-total">
          {count} active
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-[12px] text-steel"
             data-testid="widget-holds-empty">
          No active holds
        </div>
      ) : (
        <div className="mt-2 divide-y divide-chrome-mist dark:divide-graphite">
          {rows.map((r) => <Row key={r.id} r={r} />)}
        </div>
      )}

      <Link
        to="/tours"
        data-testid="widget-holds-view-all"
        className="mt-3 flex items-center justify-end gap-1 text-[11px] text-coach-blue hover:underline"
      >
        View All Holds <ChevronRight size={12} />
      </Link>
    </div>
  );
}
